import { FC } from 'react'

import { Button } from 'components/atoms/Button'

import { ProcessBadge } from '../ProcessBadge'

export interface NotificationRecipientCard {
  partnerId: string
  partnerCode?: string
  partnerName?: string
  serviceName: string
  serviceNameDescription: string
  tag: string
  recipientCount: number
  activeRecipientCount: number
  lastModifiedBy?: string
  lastModifiedOn?: string
}

export interface EmailAlertReportCardProps {
  card: NotificationRecipientCard
  showPartnerLabel: boolean
  canEdit: boolean
  onManageClick: (card: NotificationRecipientCard) => void
  onEditClick: (card: NotificationRecipientCard) => void
  onDeleteClick: (card: NotificationRecipientCard) => void
}

export const EmailAlertReportCard: FC<EmailAlertReportCardProps> = ({
  card,
  showPartnerLabel,
  canEdit,
  onManageClick,
  onEditClick,
  onDeleteClick,
}: EmailAlertReportCardProps) => {
  return (
    <div className='border border-N-200 rounded-md px-5 py-[18px] flex flex-col gap-3'>
      <div className='flex items-start gap-3.5 flex-wrap'>
        <div className='flex-1 min-w-[280px] flex flex-col gap-[5px]'>
          <div className='flex items-center gap-2.5 flex-wrap'>
            <span className='text-[15.5px] font-bold text-N-800'>
              {card.serviceNameDescription}
            </span>
            <ProcessBadge label={card.tag} />
            {showPartnerLabel && (
              <span className='text-[11px] font-semibold text-N-500 bg-N-100 rounded px-2 py-[2px]'>
                {card.partnerName} ({card.partnerCode})
              </span>
            )}
          </div>
        </div>
        <div className='flex items-center gap-2.5'>
          <span className='text-xs font-semibold text-N-600 bg-N-100 rounded-full px-[11px] py-1'>
            {card.recipientCount}{' '}
            {card.recipientCount === 1 ? 'recipient' : 'recipients'}
          </span>
          <Button
            appearance='outline'
            size='sm'
            onClick={() => onManageClick(card)}>
            <i className='ri-list-check pr-[7px]'></i>
            {canEdit ? 'Manage recipients' : 'View recipients'}
          </Button>
          {canEdit && (
            <Button
              appearance='outline'
              size='xs'
              onClick={() => onEditClick(card)}>
              <i className='ri-pencil-line'></i>
            </Button>
          )}
          {canEdit && (
            <Button
              appearance='danger'
              size='xs'
              onClick={() => onDeleteClick(card)}>
              <i className='ri-delete-bin-6-line'></i>
            </Button>
          )}
        </div>
      </div>

      {card.recipientCount === 0 && (
        <div className='border-[1.5px] border-dashed border-N-200 rounded-md px-[18px] py-[15px] flex items-center gap-3.5 flex-wrap'>
          <span className='text-[13px] text-N-500'>
            No recipients configured — this report will not be emailed
            {card.partnerName ? ` for ${card.partnerName}` : ''}.
          </span>
          {canEdit && (
            <Button
              appearance='outline'
              size='xs'
              onClick={() => onManageClick(card)}>
              + Add first recipient
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default EmailAlertReportCard
